/**
 * Email Verification Page
 * 
 * Handles email verification tokens from:
 * 1. New account signup
 * 2. Email change verification
 * 
 * URL: /verify-email?token=xxx
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { profileAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, checkAuth } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired, already_verified
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided. Please check your email link.');
      return;
    }
    
    verifyToken();
  }, [token]);
  
  const verifyToken = async () => {
    setStatus('verifying');
    
    try {
      const result = await profileAPI.confirmEmailVerification(token);
      
      if (result.message?.includes('already verified')) {
        setStatus('already_verified');
        setMessage('Your email has already been verified.');
      } else {
        setStatus('success');
        setMessage('Your email has been verified successfully!');
        
        // Refresh auth context to get updated user info
        if (isAuthenticated) {
          await checkAuth();
        }
      }
    } catch (err) {
      // Handle API error response
      // APIError sets message as constructor param, also check data.detail
      let errorMsg = 'Verification failed';
      
      if (err.message && err.message !== 'Unknown error') {
        errorMsg = err.message;
      } else if (err.data?.detail) {
        errorMsg = err.data.detail;
      }
      
      if (errorMsg.includes('expired')) {
        setStatus('expired');
        setMessage('This verification link has expired. Please request a new one.');
      } else if (errorMsg.includes('Invalid')) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email or request a new link.');
      } else {
        setStatus('error');
        setMessage(errorMsg);
      }
    }
  };
  
  const handleResendVerification = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/my-account' } } });
      return;
    }
    
    setResending(true);
    try {
      await profileAPI.sendVerificationEmail();
      setMessage('A new verification email has been sent. Please check your inbox.');
      setStatus('resent');
    } catch (err) {
      setMessage(err.message || 'Failed to send verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };
  
  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email</h1>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
          </div>
        );
        
      case 'success':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/my-account')} className="w-full">
                  Go to My Account
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} className="w-full">
                  Sign In
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                Go to Home
              </Button>
            </div>
          </div>
        );
        
      case 'already_verified':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Verified</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/my-account')} className="w-full">
                  Go to My Account
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} className="w-full">
                  Sign In
                </Button>
              )}
            </div>
          </div>
        );
        
      case 'expired':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Expired</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              {isAuthenticated ? (
                <Button 
                  onClick={handleResendVerification} 
                  disabled={resending}
                  className="w-full"
                >
                  {resending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Send New Verification Email
                    </>
                  )}
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} className="w-full">
                  Sign In to Resend
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                Go to Home
              </Button>
            </div>
          </div>
        );
        
      case 'resent':
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Sent!</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Didn't receive the email? Check your spam folder or{' '}
                <button 
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="text-blue-600 hover:underline"
                >
                  try again
                </button>
              </p>
              <Button variant="outline" onClick={() => navigate('/my-account')} className="w-full">
                Go to My Account
              </Button>
            </div>
          </div>
        );
        
      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              {isAuthenticated && (
                <Button 
                  onClick={handleResendVerification} 
                  disabled={resending}
                  className="w-full"
                >
                  {resending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Send New Verification Email
                    </>
                  )}
                </Button>
              )}
              {!isAuthenticated && (
                <Button onClick={() => navigate('/login')} className="w-full">
                  Sign In
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                Go to Home
              </Button>
            </div>
          </div>
        );
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-md mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border p-8 max-w-md w-full">
          {renderContent()}
        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center py-4 text-sm text-gray-500">
        Need help? <Link to="/contact" className="text-blue-600 hover:underline">Contact Support</Link>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
